import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, MessageCircle, Plus, Send, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useCart } from "@/context/CartContext";
import { PRODUCTS, formatCad } from "@/data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/chat`;
const KEY = "dp_chat";
const HIDDEN = ["/keeper", "/payment"];

const load = () => {
  try { return JSON.parse(sessionStorage.getItem(KEY)) || { sid: null, msgs: [] }; } catch { return { sid: null, msgs: [] }; }
};

const JarChip = ({ id, t }) => {
  const { add, setOpen } = useCart();
  const [added, setAdded] = useState(false);
  const p = PRODUCTS.find((x) => x.id === id);
  if (!p) return null;
  return (
    <button data-testid={`chat-add-${id}`} onClick={() => { add(id); setAdded(true); setOpen(true); }}
      className="mt-2 mr-2 inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#B5A642]/60 rounded-sm text-[12px] text-[#E8DDC7] hover:bg-[#B5A642]/10 transition-colors">
      {added ? <Check className="w-3 h-3 text-[#8FB08A]" /> : <Plus className="w-3 h-3" />}
      {p.name} · {formatCad(p.priceCents)}{added && ` · ${t("in the basket", "au panier")}`}
    </button>
  );
};

const URL_RE = /(https?:\/\/[^\s)]*[^\s).,;:!?'"])/g;
const Linked = ({ text }) => (text || "").split(URL_RE).map((part, i) => (
  i % 2 ? <a key={i} href={part} target="_blank" rel="noreferrer" className="underline text-[#B5A642] break-all" data-testid="chat-link">{part}</a> : part
));

const Bubble = ({ m, t }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
    <div className={`max-w-[85%] px-3.5 py-2.5 rounded-sm text-[14px] leading-relaxed ${m.role === "user" ? "bg-[#4B3226] text-[#E8DDC7]" : "bg-[#232826] text-[#D3CEC4]"}`}>
      <p className="whitespace-pre-line"><Linked text={m.text} /></p>
      {m.role === "assistant" && (m.products || []).map((id) => <JarChip key={id} id={id} t={t} />)}
    </div>
  </div>
);

export const ChatWidget = () => {
  const { pathname } = useLocation();
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(load);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const end = useRef(null);

  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify(state)); }, [state]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [state.msgs, open, busy]);

  if (HIDDEN.some((p) => pathname.startsWith(p))) return null;
  const onProduct = /^(\/fr)?\/products\//.test(pathname);

  const send = async (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg || busy) return;
    setText(""); setError(""); setBusy(true);
    setState((s) => ({ ...s, msgs: [...s.msgs, { role: "user", text: msg }] }));
    try {
      const { data } = await axios.post(API, { session_id: state.sid, message: msg, lang, page: pathname }, { withCredentials: true });
      setState((s) => ({ sid: data.session_id, msgs: [...s.msgs, { role: "assistant", text: data.reply, products: data.products || [] }] }));
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : t("The Apothecary is away from the counter. Please try again.", "L'Apothicaire s'est absenté du comptoir. Veuillez réessayer."));
    } finally { setBusy(false); }
  };
  const fresh = () => { setState({ sid: null, msgs: [] }); setError(""); };

  return (
    <div data-no-translate className="keep-brass">
      <AnimatePresence>
        {open && (
          <motion.div key="panel" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }} transition={{ duration: 0.25 }}
            className="fixed z-[58] bottom-0 right-0 sm:bottom-24 sm:right-6 w-full sm:w-[380px] h-[78vh] sm:h-[560px] bg-[#1C1F1E] border border-[#303B2D] sm:rounded-sm shadow-2xl flex flex-col"
            role="dialog" aria-label={t("Ask the Apothecary", "Demandez à l'Apothicaire")} data-testid="chat-panel">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#303B2D]">
              <div>
                <p className="font-display text-xl text-[#E8DDC7]">{t("Ask the Apothecary", "Demandez à l'Apothicaire")}</p>
                <p className="text-[12px] text-[#D3CEC4]/60">{t("Jars, measures, shipping. For health questions, your vet comes first.", "Pots, mesures, livraison. Pour la santé, votre vétérinaire d'abord.")}</p>
              </div>
              <button onClick={() => setOpen(false)} aria-label={t("Close", "Fermer")} data-testid="chat-close" className="p-1 text-[#D3CEC4]/60 hover:text-[#E8DDC7] transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {state.msgs.length === 0 && (
                <div className="space-y-2" data-testid="chat-starters">
                  <p className="font-display italic text-[16px] text-[#D3CEC4]/75">{t("Good day. What can I help you find?", "Bonjour. Que puis-je vous aider à trouver?")}</p>
                  {[t("Which jar for a senior big dog?", "Quel pot pour un grand chien âgé?"), t("How much do I give a 60 lb dog?", "Combien pour un chien de 60 lb?"), t("How much is shipping?", "Combien coûte la livraison?")].map((q) => (
                    <button key={q} onClick={() => setText(q)} className="block text-left text-[13px] text-[#B5A642] hover:text-[#E8DDC7] transition-colors">{q}</button>
                  ))}
                </div>
              )}
              {state.msgs.map((m, i) => <Bubble key={i} m={m} t={t} />)}
              {busy && <p className="flex items-center gap-2 text-[13px] text-[#D3CEC4]/55" data-testid="chat-thinking"><Loader2 className="w-3.5 h-3.5 animate-spin" />{t("Consulting the shelves…", "On consulte les étagères…")}</p>}
              {error && <p className="text-[13px] text-[#C9876B]" data-testid="chat-error">{error}</p>}
              <div ref={end} />
            </div>
            <form onSubmit={send} className="border-t border-[#303B2D] p-3 flex gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} maxLength={600} data-testid="chat-input"
                placeholder={t("Ask about a jar, a measure, shipping…", "Une question sur un pot, une mesure, la livraison…")}
                className="flex-1 min-w-0 bg-transparent border border-[#303B2D] rounded-sm px-3 py-2.5 text-[14px] text-[#E8DDC7] focus:outline-none focus:border-[#B5A642]/70 transition-colors" />
              <button type="submit" disabled={busy || !text.trim()} aria-label={t("Send", "Envoyer")} data-testid="chat-send"
                className="px-3.5 bg-[#4B3226] text-[#E8DDC7] rounded-sm hover:brightness-125 disabled:opacity-40 transition-[filter,opacity]"><Send className="w-4 h-4" /></button>
            </form>
            <div className="px-4 pb-3 flex justify-between text-[11px] text-[#D3CEC4]/50">
              <span>{t("Answers are saved to help us improve.", "Les réponses sont conservées pour nous améliorer.")}</span>
              {state.msgs.length > 0 && <button onClick={fresh} data-testid="chat-new" className="hover:text-[#E8DDC7] transition-colors">{t("New conversation", "Nouvelle conversation")}</button>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-open" aria-label={t("Ask the Apothecary", "Demandez à l'Apothicaire")}
          className={`fixed z-[57] bottom-4 right-4 sm:bottom-6 sm:right-6 ${onProduct ? "hidden md:flex" : "flex"} items-center gap-2 p-2.5 sm:pl-4 sm:pr-5 sm:py-3 rounded-full bg-[#B5A642] text-[#1C1F1E] shadow-xl hover:brightness-110 hover:-translate-y-0.5 transition-[filter,transform] duration-300`}>
          <MessageCircle className="w-4 h-4" />
          <span className="hidden sm:inline text-[12px] uppercase tracking-[0.18em] font-medium">{t("Ask the Apothecary", "Demandez à l'Apothicaire")}</span>
        </button>
      )}
    </div>
  );
};
